import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  Check,
  Clock3,
  FileText,
  Globe2,
  MapPin,
  Paperclip,
  Trash2,
  UsersRound,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useClients, useEventCategories, useProjects, useUsers } from "@/lib/hooks";
import { fileToDataUrl } from "@/lib/files";
import { EVENT_TYPES } from "@/lib/constants";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserAvatar } from "@/components/UserAvatar";
import { EventCategoryPicker } from "@/components/EventCategoryPicker";

const emptyEvent = {
  title: "",
  type: "REUNIAO",
  categoryId: "",
  categoryName: "",
  color: "",
  date: "",
  endDate: "",
  startTime: "09:00",
  endTime: "10:00",
  allDay: false,
  timezone: "Europe/Lisbon",
  location: "",
  notes: "",
  attachments: [],
  assigneeIds: [],
  clientId: "",
  projectId: "",
  status: "RASCUNHO",
};

function nextDay(date) {
  if (!date) return "";
  const next = new Date(`${date}T12:00:00`);
  next.setDate(next.getDate() + 1);
  return next.toISOString().slice(0, 10);
}

function dayCountLabel(date, endDate) {
  if (!date || !endDate) return "";
  const days = Math.round((new Date(endDate) - new Date(date)) / 86400000) + 1;
  if (days < 1) return "A data de fim é anterior ao início";
  return days === 1 ? "1 dia" : `${days} dias`;
}

function durationLabel(startTime, endTime) {
  if (!startTime || !endTime) return "";
  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);
  const minutes = Math.max(0, endHour * 60 + endMinute - startHour * 60 - startMinute);
  if (!minutes) return "0 min";
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (!hours) return `${remaining} min`;
  return remaining ? `${hours}h ${remaining}min` : `${hours}h`;
}

export function EventModal({ open, onOpenChange, event, defaultDate, defaultUserId, defaultProjectId }) {
  const queryClient = useQueryClient();
  const { data: users = [] } = useUsers();
  const { data: clients = [] } = useClients();
  const { data: categories = [] } = useEventCategories();
  const { data: projects = [] } = useProjects();
  const [form, setForm] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (event) setForm({ ...emptyEvent, ...event, attachments: event.attachments || [] });
    else {
      setForm({
        ...emptyEvent,
        date: defaultDate || "",
        assigneeIds: defaultUserId ? [defaultUserId] : [],
        projectId: defaultProjectId || "",
      });
    }
  }, [open, event, defaultDate, defaultUserId, defaultProjectId]);

  useEffect(() => {
    if (!open || form.categoryId || !categories.length) return;
    const legacyName = EVENT_TYPES[event?.type]?.label;
    const legacyCategory = event && categories.find((category) => (
      category.id === event.type?.toLowerCase()
      || category.name.toLowerCase() === legacyName?.toLowerCase()
    ));
    const initial = legacyCategory
      || categories.find((category) => category.id === "reuniao")
      || categories[0];
    setForm((current) => ({
      ...current,
      categoryId: initial.id,
      categoryName: initial.name,
      type: initial.id,
      color: initial.color,
    }));
  }, [open, event, form.categoryId, categories]);

  const duration = useMemo(
    () => durationLabel(form.startTime, form.endTime),
    [form.startTime, form.endTime],
  );
  // Um evento é de vários dias quando tem data de fim; os antigos não a têm.
  const multiDay = Boolean(form.endDate);
  const spanLabel = useMemo(
    () => dayCountLabel(form.date, form.endDate),
    [form.date, form.endDate],
  );
  const selectedCategory = useMemo(
    () => categories.find((category) => category.id === form.categoryId),
    [categories, form.categoryId],
  );
  const eventColor = selectedCategory?.color || form.color || EVENT_TYPES[form.type]?.color || "#6B4F91";
  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const toggleMultiDay = (checked) => setForm((current) => ({
    ...current,
    // Ao ligar, propõe o dia seguinte como fim; ao desligar, volta a um só dia.
    endDate: checked
      ? (current.endDate || nextDay(current.date))
      : "",
  }));

  const toggleAssignee = (userId) => {
    const assignees = form.assigneeIds || [];
    set(
      "assigneeIds",
      assignees.includes(userId)
        ? assignees.filter((id) => id !== userId)
        : [...assignees, userId],
    );
  };

  const addAttachment = async (inputEvent) => {
    const file = inputEvent.target.files?.[0];
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    const attachment = {
      id: globalThis.crypto?.randomUUID?.() || `attachment-${Date.now()}`,
      name: file.name,
      format: file.type || file.name.split(".").pop() || "",
      dataUrl,
    };
    set("attachments", [...(form.attachments || []), attachment]);
    inputEvent.target.value = "";
  };

  const save = async (status) => {
    if (!form.title.trim()) return toast.error("Indique um título");
    if (!form.date) return toast.error("Indique a data");
    if (form.endDate && form.endDate < form.date) return toast.error("O último dia é anterior ao primeiro");
    if (!form.categoryId) return toast.error("Selecione uma categoria");
    if (!(form.assigneeIds || []).length) return toast.error("Selecione pelo menos uma pessoa");
    setSaving(true);
    try {
      const payload = {
        ...form,
        status: status || form.status,
        type: selectedCategory?.id || form.type,
        categoryName: selectedCategory?.name || form.categoryName,
        color: eventColor,
        clientId: form.clientId === "none" ? "" : form.clientId,
      };
      if (event?.id) await api.put(`/events/${event.id}`, payload);
      else await api.post("/events", payload);
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success(status === "PUBLICADO" ? "Evento publicado" : "Rascunho guardado");
      onOpenChange(false);
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/events/${event.id}`);
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success("Evento eliminado");
      onOpenChange(false);
    } catch (error) {
      toast.error(apiError(error.response?.data?.detail));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[540px] p-0 gap-0 flex flex-col" data-testid="event-modal">
        <SheetHeader className="px-6 py-5 pr-14 border-b border-border">
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: eventColor }}
            />
            <SheetTitle className="font-poppins">{event?.id ? "Editar evento" : "Novo evento"}</SheetTitle>
          </div>
          <SheetDescription className="sr-only">Detalhes do evento</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <section className="space-y-4">
            {multiDay ? (
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                <div>
                  <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-primary" />Primeiro dia</Label>
                  <Input type="date" value={form.date} onChange={(inputEvent) => set("date", inputEvent.target.value)} className="mt-1.5 h-11" data-testid="event-date" />
                </div>
                <div className="h-11 flex items-center text-muted-foreground">até</div>
                <div>
                  <Label>Último dia</Label>
                  <Input type="date" min={form.date} value={form.endDate} onChange={(inputEvent) => set("endDate", inputEvent.target.value)} className="mt-1.5 h-11" data-testid="event-end-date" />
                </div>
                {spanLabel && <div className="col-span-3 text-right text-xs font-medium text-muted-foreground">{spanLabel}</div>}
              </div>
            ) : (
              <div>
                <Label className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-primary" />Data</Label>
                <Input type="date" value={form.date} onChange={(inputEvent) => set("date", inputEvent.target.value)} className="mt-1.5 h-11" data-testid="event-date" />
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <label className="h-11 px-3 rounded-lg border border-border flex items-center gap-2 text-sm">
                Vários dias
                <Switch checked={multiDay} onCheckedChange={toggleMultiDay} data-testid="event-multiday" />
              </label>
              <label className="h-11 px-3 rounded-lg border border-border flex items-center gap-2 text-sm">
                Dia inteiro
                <Switch checked={form.allDay} onCheckedChange={(checked) => set("allDay", checked)} data-testid="event-allday" />
              </label>
            </div>

            {!form.allDay && (
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                <div>
                  <Label className="flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5 text-primary" />Início</Label>
                  <Input type="time" value={form.startTime} onChange={(inputEvent) => set("startTime", inputEvent.target.value)} className="mt-1.5 h-11" />
                </div>
                <div className="h-11 flex items-center text-muted-foreground">até</div>
                <div>
                  <Label>Fim</Label>
                  <Input type="time" value={form.endTime} onChange={(inputEvent) => set("endTime", inputEvent.target.value)} className="mt-1.5 h-11" />
                </div>
                {duration && <div className="col-span-3 text-right text-xs font-medium text-muted-foreground">Duração: {duration}{multiDay ? " em cada dia" : ""}</div>}
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-primary">
              <Globe2 className="h-4 w-4" /> Europe/Lisbon
            </div>
          </section>

          <div className="border-t border-border" />

          <section className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input
                autoFocus
                value={form.title}
                onChange={(inputEvent) => set("title", inputEvent.target.value)}
                className="mt-1.5 h-11"
                placeholder="O que vai acontecer?"
                data-testid="event-title"
              />
            </div>

            <div>
              <Label>Cliente (Job)</Label>
              <Select value={form.clientId || "none"} onValueChange={(value) => set("clientId", value)}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="event-client"><SelectValue placeholder="Sem cliente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem cliente</SelectItem>
                  {clients.map((client) => <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Categoria</Label>
              <EventCategoryPicker
                categories={categories}
                value={form.categoryId}
                onSelect={(category) => setForm((current) => ({
                  ...current,
                  categoryId: category?.id || "",
                  categoryName: category?.name || "",
                  type: category?.id || current.type,
                  color: category?.color || current.color,
                }))}
              />
            </div>

            <div>
              <Label>Projeto ou campanha</Label>
              <Select value={form.projectId || "none"} onValueChange={(value) => set("projectId", value === "none" ? "" : value)} disabled={Boolean(defaultProjectId)}>
                <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Sem projeto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem projeto</SelectItem>
                  {projects.filter((project) => !form.clientId || form.clientId === "none" || project.clientId === form.clientId).map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><UsersRound className="h-3.5 w-3.5 text-primary" />Pessoas</Label>
              <div className="mt-2 rounded-xl border border-border p-2.5 flex flex-wrap gap-2">
                {users.map((member) => {
                  const active = (form.assigneeIds || []).includes(member.id);
                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => toggleAssignee(member.id)}
                      className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border text-xs transition-colors ${active ? "border-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"}`}
                      data-testid={`event-assignee-${member.id}`}
                    >
                      <UserAvatar user={member} size="h-6 w-6" />
                      {member.name}
                      {active && <Check className="h-3 w-3 text-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" />Local ou link</Label>
              <Input value={form.location} onChange={(inputEvent) => set("location", inputEvent.target.value)} className="mt-1.5 h-11" placeholder="Morada ou link de videochamada" />
            </div>

            <div>
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(inputEvent) => set("notes", inputEvent.target.value)} className="mt-1.5 min-h-[110px]" />
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5 text-primary" />Anexos</Label>
              <div className="mt-2 space-y-2">
                {(form.attachments || []).map((attachment) => (
                  <div key={attachment.id} className="rounded-lg border border-border px-3 py-2 flex items-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="truncate flex-1">{attachment.name}</span>
                    <button type="button" onClick={() => set("attachments", form.attachments.filter((item) => item.id !== attachment.id))} className="text-muted-foreground hover:text-destructive" aria-label="Remover anexo">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <label className="inline-flex items-center gap-2 text-sm text-primary cursor-pointer hover:underline">
                  <Paperclip className="h-4 w-4" /> Anexar ficheiro
                  <input type="file" className="hidden" onChange={addAttachment} />
                </label>
              </div>
            </div>
          </section>
        </div>

        <SheetFooter className="border-t border-border bg-card px-6 py-4 flex-row items-center gap-2 sm:space-x-0">
          {event?.id && (
            <Button variant="ghost" size="icon" onClick={remove} className="text-destructive hover:text-destructive mr-auto" data-testid="event-delete" aria-label="Eliminar evento">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" onClick={() => save("RASCUNHO")} disabled={saving} className={!event?.id ? "ml-auto" : ""} data-testid="event-draft">
            Guardar rascunho
          </Button>
          <Button onClick={() => save("PUBLICADO")} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="event-publish">
            Publicar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
